import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Spinner from '../components/atoms/Spinner';
import usePackPermissions from '../hooks/usePackPermissions';

// Lazy-loaded pages for code splitting
const ChangePassword    = lazy(() => import('../pages/ChangePassword'));
const Home              = lazy(() => import('../pages/Home'));
const Dashboard         = lazy(() => import('../pages/Dashboard'));
const Vehicles          = lazy(() => import('../pages/Vehicles'));
const Clients           = lazy(() => import('../pages/Clients'));
const Reservations      = lazy(() => import('../pages/Reservations'));
const Assurances        = lazy(() => import('../pages/Assurances'));
const Contrats          = lazy(() => import('../pages/Contrats'));
const Factures          = lazy(() => import('../pages/Factures'));
const Cautions          = lazy(() => import('../pages/Cautions'));
const Vidanges          = lazy(() => import('../pages/Vidanges'));
const VisitesTechniques = lazy(() => import('../pages/VisitesTechniques'));
const Financements      = lazy(() => import('../pages/Financements'));
const Settings          = lazy(() => import('../pages/Settings'));
const AdminTenants      = lazy(() => import('../pages/AdminTenants'));
const AdminUsers        = lazy(() => import('../pages/AdminUsers'));
const AdminSuperAdmins  = lazy(() => import('../pages/AdminSuperAdmins'));
const AdminAgents       = lazy(() => import('../pages/AdminAgents'));
const NotFound          = lazy(() => import('../pages/NotFound'));
const SignaturePage     = lazy(() => import('../pages/SignaturePage'));

const PageLoader = () => (
  <div className="flex items-center justify-center h-48">
    <Spinner size="lg" />
  </div>
);

// Protection SuperAdmin
const SuperAdminRoute = ({ children }) => {
  const user = useSelector(s => s.auth.user);
  if (user?.role !== 'SuperAdmin') return <Navigate to="/" replace />;
  return children;
};

// Protection SuperAdmin ou Agent
const AdminOrAgentRoute = ({ children }) => {
  const user = useSelector(s => s.auth.user);
  if (user?.role !== 'SuperAdmin' && user?.role !== 'Agent') return <Navigate to="/" replace />;
  return children;
};

// Bloque toutes les routes si forcePasswordChange est actif
const ProtectedRoute = ({ children }) => {
  const user = useSelector(s => s.auth.user);
  if (user?.forcePasswordChange) return <Navigate to="/change-password" replace />;
  return children;
};

// Bloque l'accès si la fonctionnalité n'est pas incluse dans le pack
const PackRoute = ({ feature, children }) => {
  const { canAccess } = usePackPermissions();
  if (!canAccess(feature)) return <Navigate to="/" replace />;
  return children;
};

// Redirige SuperAdmin et Agent vers leurs espaces dédiés pour les routes tenant-only
const TenantOnlyRoute = ({ children }) => {
  const user = useSelector(s => s.auth.user);
  if (user?.role === 'SuperAdmin') return <Navigate to="/admin/superadmins" replace />;
  if (user?.role === 'Agent') return <Navigate to="/admin/tenants" replace />;
  return children;
};

// Page d'accueil unifiée pour tous les packs
const DashboardRoute = () => <Home />;

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Changement de mot de passe obligatoire */}
      <Route path="/change-password" element={<ChangePassword />} />

      {/* Routes protégées — tenant only (SuperAdmin redirigé vers /admin/superadmins) */}
      <Route path="/"                   element={<ProtectedRoute><TenantOnlyRoute><DashboardRoute /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/vehicles"           element={<ProtectedRoute><TenantOnlyRoute><Vehicles /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/clients"            element={<ProtectedRoute><TenantOnlyRoute><Clients /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/reservations"       element={<ProtectedRoute><TenantOnlyRoute><Reservations /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/contrats"           element={<ProtectedRoute><TenantOnlyRoute><Contrats /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/factures"           element={<ProtectedRoute><TenantOnlyRoute><Factures /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/cautions"           element={<ProtectedRoute><TenantOnlyRoute><Cautions /></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/financements"       element={<ProtectedRoute><TenantOnlyRoute><PackRoute feature="financement"><Financements /></PackRoute></TenantOnlyRoute></ProtectedRoute>} />

      {/* Routes conditionnelles au pack (maintenance = Pro/Pro+) */}
      <Route path="/assurances"         element={<ProtectedRoute><TenantOnlyRoute><PackRoute feature="maintenance"><Assurances /></PackRoute></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/vidanges"           element={<ProtectedRoute><TenantOnlyRoute><PackRoute feature="maintenance"><Vidanges /></PackRoute></TenantOnlyRoute></ProtectedRoute>} />
      <Route path="/visites-techniques" element={<ProtectedRoute><TenantOnlyRoute><PackRoute feature="maintenance"><VisitesTechniques /></PackRoute></TenantOnlyRoute></ProtectedRoute>} />

      <Route path="/settings"           element={<ProtectedRoute><TenantOnlyRoute><Settings /></TenantOnlyRoute></ProtectedRoute>} />

      {/* Routes SuperAdmin */}
      <Route
        path="/admin/superadmins"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <AdminSuperAdmins />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute>
            <AdminOrAgentRoute>
              <AdminTenants />
            </AdminOrAgentRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/agents"
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <AdminAgents />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminOrAgentRoute>
              <AdminUsers />
            </AdminOrAgentRoute>
          </ProtectedRoute>
        }
      />

      {/* Public signature page */}
      <Route path="/sign/:token" element={<SignaturePage />} />

      {/* Not found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
